// src/services/patientTickets.js
//
// Patient support tickets. Uses the shared authenticated axios client, so a
// failed call throws an error carrying `response.status` and the parsed
// `response.data` -- pages read it with readApiError/apiErrorCode (a message is
// never "[object Object]" or raw JSON).
//
// Attachments are uploaded BEFORE the ticket/reply is submitted: presign ->
// PUT to the signed URL -> keep the opaque `upload_token`. No storage key or
// bucket exists on this side.

import { api } from "../lib/patientApi";

const BASE = "/api/v1/patients/me/tickets";
const UPLOAD_FAILED = "The file could not be uploaded. Please try again.";

function clean(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && !(typeof value === "string" && value.trim() === ""))
  );
}

function id(value) {
  return encodeURIComponent(value);
}

export async function uploadToPresignedUrl(presign, file) {
  const headers = presign?.upload_headers || { "Content-Type": file.type || "application/octet-stream" };
  let response = null;
  try {
    response = await fetch(presign.upload_url, { method: "PUT", headers, body: file });
  } catch {
    response = null;
  }
  if (!response || !response.ok) {
    const error = new Error(UPLOAD_FAILED);
    error.uploadFailed = true;
    error.response = { status: response?.status || 0, data: { detail: UPLOAD_FAILED } };
    throw error;
  }
  return true;
}

export const patientTicketsApi = {
  async filterOptions({ signal } = {}) {
    const res = await api.get(`${BASE}/filter-options`, { signal });
    return res.data;
  },

  async subjects(category, { signal } = {}) {
    const res = await api.get(`${BASE}/subjects`, { params: clean({ category }), signal });
    return res.data;
  },

  async referenceOptions({ signal } = {}) {
    const res = await api.get(`${BASE}/reference-options`, { signal });
    return res.data;
  },

  async list(params = {}, { signal } = {}) {
    const res = await api.get(BASE, { params: clean(params), signal });
    return res.data;
  },

  async detail(ticketId, { signal } = {}) {
    const res = await api.get(`${BASE}/${id(ticketId)}`, { signal });
    return res.data;
  },

  async create(payload) {
    const res = await api.post(BASE, payload);
    return res.data;
  },

  async addMessage(ticketId, payload) {
    const res = await api.post(`${BASE}/${id(ticketId)}/messages`, payload);
    return res.data;
  },

  async changeStatus(ticketId, payload) {
    const res = await api.post(`${BASE}/${id(ticketId)}/status`, payload);
    return res.data;
  },

  async attachmentDownload(ticketId, attachmentId) {
    const res = await api.get(`${BASE}/${id(ticketId)}/attachments/${id(attachmentId)}/download`);
    return res.data;
  },

  /** Draft upload before the ticket exists. -> upload_token */
  async uploadDraftAttachment(clientRequestId, file) {
    const res = await api.post(`${BASE}/attachments/presign`, {
      client_request_id: clientRequestId,
      file_name: file.name,
      content_type: file.type || "application/octet-stream",
      size_bytes: file.size,
    });
    await uploadToPresignedUrl(res.data, file);
    return res.data.upload_token;
  },

  /** Reply upload for an existing ticket. -> upload_token */
  async uploadTicketAttachment(ticketId, file) {
    const res = await api.post(`${BASE}/${id(ticketId)}/attachments/presign`, {
      file_name: file.name,
      content_type: file.type || "application/octet-stream",
      size_bytes: file.size,
    });
    await uploadToPresignedUrl(res.data, file);
    return res.data.upload_token;
  },
};

export default patientTicketsApi;
